import * as React from 'react';
import { useEffect, useState } from 'react';

import { considerateThrow } from '@utils/monitoring';

const isSSR = typeof window === 'undefined';

export const useBreakpoint = (minWidth: number) => {
  if (isSSR) {
    considerateThrow(new Error('Do not use this when server rendering!'));
  }

  const [breakpointMet, setBreakpointMet] = useState(
    (isSSR ? 0 : window.innerWidth) > minWidth,
  );

  useEffect(() => {
    const onResize = () => {
      setBreakpointMet(window.innerWidth > minWidth);
    };

    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [minWidth]);

  return breakpointMet;
};

// Legacy browsers are covered by @vitejs/plugin-legacy in production builds.
export const useIntersectionObserver = ({
  root,
  rootMargin,
  threshold,
}: IntersectionObserverInit = {}) => {
  const [entry, updateEntry] = useState<IntersectionObserverEntry | null>(null);
  const [element, setElement] = useState<HTMLElement | null>(null);
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([currentEntry]) => updateEntry(currentEntry),
      { root, rootMargin, threshold },
    );
    observer.observe(element);

    return () => observer.disconnect();
  }, [element, root, rootMargin, threshold]);

  return [setElement, entry] as const;
};

export const useHasIntersected = (config?: IntersectionObserverInit) => {
  const [targetRef, entry] = useIntersectionObserver(config);
  const [hasIntersected, setHasIntersected] = React.useState(false);

  React.useEffect(() => {
    if (!hasIntersected && entry?.isIntersecting) {
      setHasIntersected(true);
    }
  }, [entry?.isIntersecting]);

  return {
    targetRef,
    hasIntersected,
  };
};

export const useDeferredImage = (src: string, srcSet?: string) => {
  const { targetRef, hasIntersected } = useHasIntersected({
    rootMargin: '50px',
  });
  return {
    targetRef,
    resolvedImage: hasIntersected ? { src, srcSet } : undefined,
  };
};
